import axios from 'axios';
import { User, CreateUserRequest, Expense, CreateExpenseRequest, UpdateExpenseRequest, ExpenseAnalytics, Family, UpdateFamilyRequest, Income, CreateIncomeRequest, UpdateIncomeRequest, IncomeAnalytics, FinancialSummary } from '../types';
import { API_BASE_URL } from '../config/api.config';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Helper function to get family_id from localStorage
const getFamilyId = (): string => {
  const familyData = localStorage.getItem('family');
  if (!familyData) {
    throw new Error('Family data not found. Please login again.');
  }
  try {
    const family = JSON.parse(familyData);
    if (!family.id) {
      throw new Error('Family ID not found. Please login again.');
    }
    return family.id;
  } catch (error) {
    throw new Error('Invalid family data. Please login again.');
  }
};

// Add request interceptor for authentication and logging
api.interceptors.request.use(
  (config) => {
    // Add authentication token if available
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    console.log(`Making ${config.method?.toUpperCase()} request to ${config.url}`);
    return config;
  },
  (error) => {
    console.error('Request error:', error);
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling and automatic logout
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    console.error('API Error:', error.response?.data || error.message);

    // Handle authentication errors - logout user automatically
    if (error.response?.status === 401 || error.response?.status === 403) {
      console.log('Authentication failed, logging out user...');
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('family');

      // Reload the page to trigger re-authentication
      window.location.reload();
    }

    return Promise.reject(error);
  }
);

// User API
export const userAPI = {
  getAll: async (): Promise<User[]> => {
    const familyId = getFamilyId();
    const response = await api.get(`/families/${familyId}/users`);
    return response.data;
  },

  create: async (userData: CreateUserRequest): Promise<User> => {
    const familyId = getFamilyId();
    const response = await api.post(`/families/${familyId}/users`, userData);
    return response.data;
  },

  update: async (userId: string, userData: Partial<CreateUserRequest>): Promise<User> => {
    const familyId = getFamilyId();
    const response = await api.put(`/families/${familyId}/users/${userId}`, userData);
    return response.data;
  },

  delete: async (userId: string): Promise<void> => {
    const familyId = getFamilyId();
    await api.delete(`/families/${familyId}/users/${userId}`);
  },

  getExpenses: async (userId: string): Promise<Expense[]> => {
    const familyId = getFamilyId();
    const response = await api.get(`/families/${familyId}/users/${userId}/expenses`);
    return response.data;
  },

  getIncomes: async (userId: string): Promise<Income[]> => {
    const familyId = getFamilyId();
    const response = await api.get(`/families/${familyId}/users/${userId}/incomes`);
    return response.data;
  },
};

// Expense API
export const expenseAPI = {
  getAll: async (): Promise<Expense[]> => {
    const familyId = getFamilyId();
    const response = await api.get(`/families/${familyId}/expenses`);
    return response.data;
  },

  create: async (expenseData: CreateExpenseRequest): Promise<Expense> => {
    const familyId = getFamilyId();
    const response = await api.post(`/families/${familyId}/expenses`, expenseData);
    return response.data;
  },

  update: async (id: string, expenseData: UpdateExpenseRequest): Promise<Expense> => {
    const familyId = getFamilyId();
    const response = await api.put(`/families/${familyId}/expenses/${id}`, expenseData);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    const familyId = getFamilyId();
    await api.delete(`/families/${familyId}/expenses/${id}`);
  },

  getAnalytics: async (userId?: string): Promise<ExpenseAnalytics> => {
    const familyId = getFamilyId();
    const params = userId ? { user_id: userId } : {};
    const response = await api.get(`/families/${familyId}/analytics/expenses`, { params });
    return response.data;
  },
};

// Income API
export const incomeAPI = {
  getAll: async (): Promise<Income[]> => {
    const familyId = getFamilyId();
    const response = await api.get(`/families/${familyId}/incomes`);
    return response.data;
  },

  create: async (incomeData: CreateIncomeRequest): Promise<Income> => {
    const familyId = getFamilyId();
    console.log('API: Creating income with data:', incomeData);
    console.log('API: JSON stringified:', JSON.stringify(incomeData));
    const response = await api.post(`/families/${familyId}/incomes`, incomeData);
    return response.data;
  },

  update: async (id: string, incomeData: UpdateIncomeRequest): Promise<Income> => {
    const familyId = getFamilyId();
    const response = await api.put(`/families/${familyId}/incomes/${id}`, incomeData);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    const familyId = getFamilyId();
    await api.delete(`/families/${familyId}/incomes/${id}`);
  },

  getByUser: async (userId: string): Promise<Income[]> => {
    const familyId = getFamilyId();
    const response = await api.get(`/families/${familyId}/users/${userId}/incomes`);
    return response.data;
  },

  getAnalytics: async (userId?: string): Promise<IncomeAnalytics> => {
    const familyId = getFamilyId();
    const params = userId ? { user_id: userId } : {};
    const response = await api.get(`/families/${familyId}/analytics/incomes`, { params });
    return response.data;
  },

  getFinancialSummary: async (userId?: string): Promise<FinancialSummary> => {
    const familyId = getFamilyId();
    const params = userId ? { user_id: userId } : {};
    const response = await api.get(`/families/${familyId}/analytics/financial-summary`, { params });
    return response.data;
  },
};

// Family API
export const familyAPI = {
  update: async (familyData: UpdateFamilyRequest): Promise<Family> => {
    const familyId = getFamilyId();
    const response = await api.put(`/families/${familyId}`, familyData);
    return response.data;
  },

  getCurrent: async (): Promise<Family> => {
    const familyId = getFamilyId();
    const response = await api.get(`/families/${familyId}`);
    return response.data;
  },
};

// Category API
export interface Category {
  id: string;
  family_id: string;
  name: string;
  icon: string | null;
  color: string | null;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateCategoryRequest {
  name: string;
  icon?: string;
  color?: string;
}

export interface UpdateCategoryRequest {
  name?: string;
  icon?: string;
  color?: string;
}

export const categoryAPI = {
  getAll: async (): Promise<Category[]> => {
    const familyId = getFamilyId();
    const response = await api.get(`/families/${familyId}/categories`);
    return response.data;
  },

  create: async (categoryData: CreateCategoryRequest): Promise<Category> => {
    const familyId = getFamilyId();
    const response = await api.post(`/families/${familyId}/categories`, categoryData);
    return response.data;
  },

  update: async (id: string, categoryData: UpdateCategoryRequest): Promise<Category> => {
    const familyId = getFamilyId();
    const response = await api.put(`/families/${familyId}/categories/${id}`, categoryData);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    const familyId = getFamilyId();
    await api.delete(`/families/${familyId}/categories/${id}`);
  },
};

// Tag API
export interface Tag {
  id: string;
  family_id: string;
  name: string;
  color: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateTagRequest {
  name: string;
  color?: string;
}

export interface UpdateTagRequest {
  name?: string;
  color?: string;
}

export const tagAPI = {
  getAll: async (query?: string): Promise<Tag[]> => {
    const familyId = getFamilyId();
    const params = query ? { query } : {};
    const response = await api.get(`/families/${familyId}/tags`, { params });
    return response.data;
  },

  create: async (tagData: CreateTagRequest): Promise<Tag> => {
    const familyId = getFamilyId();
    const response = await api.post(`/families/${familyId}/tags`, tagData);
    return response.data;
  },

  update: async (id: string, tagData: UpdateTagRequest): Promise<Tag> => {
    const familyId = getFamilyId();
    const response = await api.put(`/families/${familyId}/tags/${id}`, tagData);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    const familyId = getFamilyId();
    await api.delete(`/families/${familyId}/tags/${id}`);
  },

  getForExpense: async (expenseId: string): Promise<Tag[]> => {
    const familyId = getFamilyId();
    const response = await api.get(`/families/${familyId}/expenses/${expenseId}/tags`);
    return response.data;
  },
};

export default api;
